/**
 * Service layer — Document Markup (docs/DOCUMENT_MARKUP.md).
 *
 * All calls route through /api/document-markup. See that file's header for the coordinate model
 * (page-relative visual fractions, not QcMarkupModal's PDF-user-space + page_frame model) and why.
 */

const GENERATED_SOURCES = new Set(['predesign-report', 'opc-report', 'site-analysis', 'outline-spec', 'schedules'])

/**
 * The stable key markup is stored against — survives a generated document's delete-and-reinsert
 * on regenerate (only one row per (milestone, source) ever exists for those). Mirrored server-side
 * in api/document-markup.js — keep in sync.
 * @param {{ milestoneId: string, source: string, documentId: string }} doc
 */
export function subjectKeyFor({ milestoneId, source, documentId }) {
  return GENERATED_SOURCES.has(source) ? `${milestoneId}:${source}` : `doc:${documentId}`
}

function api(action, params, clerkToken) {
  const headers = { 'Content-Type': 'application/json' }
  if (clerkToken) headers['Authorization'] = `Bearer ${clerkToken}`
  return fetch('/api/document-markup', { method: 'POST', headers, body: JSON.stringify({ action, ...params }) }).then(r => r.json())
}

/**
 * Loads (lazily creating) the markup row + its notes for a document.
 * @param {{ milestoneId, source, documentId, documentUrl, documentName }} doc
 * @returns {Promise<{ markup: object, notes: object[] }>}
 */
export async function getDocumentMarkup(doc) {
  const token = await window.__clerkGetToken()
  const res = await api('get', doc, token)
  if (res.error) throw new Error(res.error)
  return res
}

/** Open markup notes per generated source on a milestone (e.g. ['outline-spec','schedules']). → { [source]: n } */
export async function getOpenNoteCounts(milestoneId, sources) {
  const token = await window.__clerkGetToken()
  const res = await api('openNotes', { milestoneId, sources }, token)
  if (res.error) throw new Error(res.error)
  return res.counts || {}
}

export async function saveDocumentAnnotation(markupId, annotationJson, actorName) {
  const token = await window.__clerkGetToken()
  const res = await api('saveAnnotation', { markupId, annotationJson, actorName }, token)
  if (res.error) throw new Error(res.error)
  return res
}

export async function createDocumentNote({ markupId, text, severity, anchor, authorMemberId, authorName }) {
  const token = await window.__clerkGetToken()
  const res = await api('createNote', { markupId, text, severity, anchor, authorMemberId, authorName }, token)
  if (res.error) throw new Error(res.error)
  return res.note
}

export async function resolveDocumentNote(noteId, actorName) {
  const token = await window.__clerkGetToken()
  const res = await api('resolveNote', { noteId, actorName }, token)
  if (res.error) throw new Error(res.error)
  return res.note
}

export async function reopenDocumentNote(noteId) {
  const token = await window.__clerkGetToken()
  const res = await api('reopenNote', { noteId }, token)
  if (res.error) throw new Error(res.error)
  return res.note
}

export async function deleteDocumentNote(noteId) {
  const token = await window.__clerkGetToken()
  const res = await api('deleteNote', { noteId }, token)
  if (res.error) throw new Error(res.error)
  return res
}

/**
 * Opens AlderQC's document markup tool in its own tab — the shared helper every "Markup & QC"
 * entry point calls (mirrors DrawingSheetList.jsx's openQcTab for sheets). `doc` is a
 * milestone_documents-shaped record; `extra` supplies context the doc row itself doesn't carry.
 * @param {{ id, url, name, source, milestone_id, locked }} doc
 * @param {{ projectId?, projectName?, milestoneName?, slackThreadTs? }} [extra]
 */
export function openDocQcTab(doc, extra = {}) {
  const params = new URLSearchParams({
    documentId: doc.id || '', documentUrl: doc.url || '', documentName: doc.name || 'Document',
    milestoneId: doc.milestone_id || '', source: doc.source || 'upload', locked: doc.locked ? '1' : '0',
    projectId: extra.projectId || '', projectName: extra.projectName || '',
    milestoneName: extra.milestoneName || '', slackThreadTs: extra.slackThreadTs || '',
  })
  window.open('/docqc?' + params.toString(), '_blank')
}

const REPORT_TITLES = { 'predesign-report': 'Pre-Design Research Report', 'opc-report': "Owner's Project Criteria" }
const REPORT_ENDPOINTS = { 'predesign-report': '/api/predesign-report', 'opc-report': '/api/opc-report' }

/**
 * Opens AlderQC's document markup tool for an APP-GENERATED document — the Pre-Design Research
 * report, OPC, or Site Analysis — without Matt (or anyone) downloading anything and re-uploading
 * it (docs/DOCUMENT_MARKUP.md, Matt: "that'd just mean generating a PDF from our own system only
 * to feed it back into it").
 *
 * If the report has already been issued (`frozen` — a real milestone_documents row from posting),
 * opens that exact copy, same as any other document. Otherwise resolves the CURRENT working
 * version and opens that instead:
 *   - Site Analysis already has a live, always-current PDF at a stable Storage path (rewritten on
 *     every save in the tool itself) — just resolved and opened, nothing rendered here.
 *   - Pre-Design Research / OPC have no persisted "current" copy pre-issue (the Preview button
 *     renders on demand into a throwaway blob URL that can't cross tabs) — this renders one fresh
 *     server-side and stores it at a stable, always-overwritten draft path so the /docqc tab has
 *     a real URL to open. Not a milestone_documents row (that would misread as "Issued").
 * @param {{ source, msId, projectId, projectName, msName, slackThreadTs, frozen? }} ctx
 * @returns {Promise<{ ok: true } | { error: string }>}
 */
export async function openDocQcTabForGenerated({ source, msId, projectId, projectName, msName, slackThreadTs, frozen }) {
  if (frozen && frozen.url) {
    openDocQcTab(frozen, { projectId, projectName, milestoneName: msName, slackThreadTs })
    return { ok: true }
  }
  // Open the tab synchronously (before the await below) so the browser doesn't block it as a
  // popup — same pattern as PreDesignReportHost.jsx — then point it at /docqc once resolved.
  const tab = window.open('', '_blank')
  if (tab) {
    try {
      tab.document.write('<body style="margin:0;font-family:\'DM Sans\',sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#1a2629;color:#cfd8da;font-size:13px;text-align:center;padding:24px;box-sizing:border-box;">Opening AlderQC…</body>')
      tab.document.close()
    } catch { /* best-effort — the tab just stays blank for this brief resolve step */ }
  }
  try {
    let url = null
    if (source === 'site-analysis') {
      const token = await window.__clerkGetToken()
      const res = await fetch('/api/site-analyses?projectId=' + encodeURIComponent(projectId), { headers: { Authorization: 'Bearer ' + token } }).then(r => r.json())
      if (res.error) throw new Error(res.error)
      url = res.analysis?.pdfUrl
    } else if (REPORT_ENDPOINTS[source]) {
      const token = await window.__clerkGetToken()
      const res = await fetch(REPORT_ENDPOINTS[source], {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ projectId, milestoneId: msId, draft: true }),
      }).then(r => r.json())
      if (res.error) throw new Error(res.error)
      url = res.url
    }
    if (!url) throw new Error('Nothing to markup yet — open the tool and save something first.')
    const params = new URLSearchParams({
      documentUrl: url, documentName: REPORT_TITLES[source] || 'Document',
      milestoneId: msId || '', source: source || '', locked: '0',
      projectId: projectId || '', projectName: projectName || '', milestoneName: msName || '', slackThreadTs: slackThreadTs || '',
    })
    if (tab) tab.location = '/docqc?' + params.toString()
    else window.open('/docqc?' + params.toString(), '_blank')
    return { ok: true }
  } catch (err) {
    if (tab) tab.close()
    return { error: err.message || 'Could not open the markup tool.' }
  }
}

/**
 * Presentation review copy (docs/DOCUMENT_MARKUP.md "Presentation review copies"). The design
 * presentation is a live web page, so the server photographs it section by section, exactly as
 * the client sees it, and saves a NEW dated PDF on the milestone (source 'presentation-review' —
 * internal, never shown to the client). The copy then opens in AlderQC like any other document.
 * Takes up to a minute, so a placeholder tab opens immediately (popup-blocker safe).
 * @param {{ msId, projectId, projectName, msName, slackThreadTs }} ctx
 * @returns {Promise<{ document?: object, error?: string }>}
 */
export async function makePresentationReviewCopy({ msId, projectId, projectName, msName, slackThreadTs }) {
  const tab = window.open('', '_blank')
  if (tab) {
    try {
      tab.document.write('<body style="margin:0;font-family:\'DM Sans\',sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#1a2629;color:#cfd8da;font-size:13px;text-align:center;padding:24px;box-sizing:border-box;">Making a review copy of the presentation. This takes up to a minute.</body>')
      tab.document.close()
    } catch { /* best-effort */ }
  }
  try {
    const token = await window.__clerkGetToken()
    const res = await fetch('/api/presentation-review-copy', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ msId }),
    }).then(r => r.json().catch(() => ({ error: 'The review copy took too long or failed (HTTP ' + r.status + ').' })))
    if (res.error || !res.document) throw new Error(res.error || 'The review copy was not saved.')
    const doc = res.document
    const params = new URLSearchParams({
      documentId: doc.id, documentUrl: doc.url, documentName: doc.name || 'Review copy',
      milestoneId: msId, source: doc.source, locked: '0',
      projectId: projectId || '', projectName: projectName || '', milestoneName: msName || '', slackThreadTs: slackThreadTs || '',
    })
    if (tab) tab.location = '/docqc?' + params.toString()
    else window.open('/docqc?' + params.toString(), '_blank')
    return { document: doc }
  } catch (err) {
    if (tab) tab.close()
    return { error: err.message || String(err) }
  }
}
